import { useEffect, useMemo, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
// Icons
import { AiOutlineClose } from 'react-icons/ai';
import { FaEye, FaFilter, FaPlus, FaRegEdit } from 'react-icons/fa';
import { IoArchiveOutline } from 'react-icons/io5';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { TableSelect } from '../../../../Components/ReactTable/components/TableSelect';
import DefaultColumnFilter from '../../../../Components/ReactTable/Filters/DefaultColumnFilter';
import ReactTableRender from '../../../../Components/ReactTable/ReactTableRender';
import SearchBox from '../../../../Components/SearchBox';
import { ThematicAreaBadge } from '../../../../Components/ThematicAreaBadge';

import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import { debounce, paramsGenerator } from '../../../../_helpers/_methods';
import { CreateEventLink, TargetedParty } from './components/style';

// components
import EventsTableNav from './EventsTableNav';
import FilterOffCanvas from './FilterOffCanvas';
import { checkPermission } from '../../../../_helpers/roles/check-roles';
import NepaliDate from 'nepali-date-converter';
import { useRef } from 'react';

const StyledFilterBadge = styled.div`
  background-color: #f0f0f0;
  border-color: #bababa;
`;
export const FilterBadge = ({ title, clearFilter }) => {
  return (
    <StyledFilterBadge className=" d-flex align-items-center justify-content-between border border-2 border-secondary rounded-pill m-1 py-1 px-3">
      <div className="me-3">{title}</div>
      <span className="pointer-cursor">
        <AiOutlineClose size={12} onClick={clearFilter} />
      </span>
    </StyledFilterBadge>
  );
};

const INITIALSTATE = {
  IsMtot: false,
  IsVirtual: false,
  IsPhysical: false,
  EventType: '',
  ThematicAreas: [],
  TargetedProvinces: [],
  Organizer: '',
  Sectors: []
};
function EventsTable() {
  const intl = useIntl();

  const ManualFilterByName = ({ column: { filterValue, setFilter } }) => {
    const inputRef = useRef(null);
    const [name, setName] = useState(filterValue);
    const handleNameChange = useAsyncDebounce((value) => {
      setFilter(value || undefined);
    }, 800);
    return (
      <input
        ref={inputRef}
        value={name || ''}
        onChange={(e) => {
          setName(e.target.value);
          handleNameChange(e.target.value);
        }}
        placeholder={intl.formatMessage({ id: 'filter.search' })}
      />
    );
  };

  const permissions = useSelector((state) => state.user.permissions);
  const user = useSelector((state) => state.user.user);
  const lang = useSelector((state) => state.local.local);

  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchEventParam, setSearchEventParam] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [activeTab, setActiveTab] = useState('my_events');
  const [navItems, setNavItems] = useState([]);
  const [eventStateType, setEventStateType] = useState({
    value: '',
    label: <FormattedMessage id="eventtab.allEvents" />
  });
  const [eventFilters, setEventFilters] = useState(INITIALSTATE);
  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );
  const gotoPage = (page) => {
    setPageNum(page);
  };

  let userActiveRole = user?.active_role;
  const columns = useMemo(
    () => [
      {
        Header: <FormattedMessage id="Events" />,
        accessor: 'eventName',
        isSorted: true,
        Filter: ManualFilterByName,
        Cell: ({ row: { original } }) => (
          <span className="d-block">
            <Link to={`${original.id}`}>
              <strong className="text-primary text-break">
                {lang === 'ne' ? original.nepali_name : original.english_name}
              </strong>
            </Link>
            <span className="d-inline-block" style={{ position: 'relative', bottom: '0.5rem' }}>
              {original.mtot ? (
                <span className="bg-success text-light px-1 rounded small">MTOT</span>
              ) : null}
            </span>
            <p className="d-block small text-dark mb-1">
              [{' '}
              {new NepaliDate(new Date(original.start_date)).format(
                'DD MMMM',
                lang === 'ne' ? 'np' : 'en'
              )}
              <span className="text-secondary "> - </span>
              {new NepaliDate(new Date(original.end_date)).format(
                'DD MMMM',
                lang === 'ne' ? 'np' : 'en'
              )}{' '}
              ]
            </p>
            <span className="badge bg-secondary">#EV-{original.id}</span>
            {original.completed ? (
              <div>
                <span className="badge bg-success">Completed</span>
              </div>
            ) : null}
          </span>
        ),
        width: '18%'
      },
      {
        Header: <FormattedMessage id="event.organized_by" />,
        isSorted: true,
        accessor: 'organizer',
        Cell: ({ row: { original } }) => (
          <span>
            <span className="d-block">{original?.organizer_employeeType}</span>
            <span className="d-block small">{original?.organizer_province}</span>
            <span className="d-block small">{original?.organizer_user}</span>
          </span>
        ),
        width: '7%'
      },
      {
        Header: <FormattedMessage id="event.sessions" />,
        accessor: 'seasons',
        isSorted: true,
        width: '6%'
      },
      {
        Header: <FormattedMessage id="event.targeted_for" />,
        accessor: 'targeted_for',
        isSorted: true,
        Filter: TargetedForSelectColumnFilter,
        filter: 'includes',
        Cell: ({ row: { original } }) => (
          <span className="d-flex align-items-center flex-wrap">
            {original?.display_targeted_for && (
              <ul>
                {original?.display_targeted_for.slice(0, 3).map((target, index) => (
                  <TargetedParty key={index}>
                    {target?.title} {target?.parent__title ? `(${target?.parent__title})` : ''}
                  </TargetedParty>
                ))}
                {original?.display_targeted_for.length > 3 ? (
                  <span className="small text-secondary">
                    +{original?.display_targeted_for.length - 3} more
                  </span>
                ) : null}
              </ul>
            )}
          </span>
        ),
        width: '13%'
      },
      {
        Header: <FormattedMessage id="thematic_area" />,
        accessor: 'thematic_areas',
        isSorted: true,
        Filter: ThemanticAreaSelectColumnFilter,
        filter: 'includes',
        Cell: ({ row, cell: { value } }) => (
          <span className="d-flex align-items-center flex-wrap">
            {Boolean(value) && (
              <>
                {row.isExpanded ? (
                  <div {...row.getToggleRowExpandedProps()}>
                    <span>👇</span>
                    <div>
                      {value.map((area, index) => (
                        <ThematicAreaBadge key={index}>{area}</ThematicAreaBadge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <ul>
                    {value.slice(0, 3).map((target, index) => (
                      <ThematicAreaBadge key={index}>{target}</ThematicAreaBadge>
                    ))}
                    {value.length > 3 ? (
                      <span className="small text-secondary" {...row.getToggleRowExpandedProps()}>
                        +{value.length - 3} more 👉
                      </span>
                    ) : null}
                  </ul>
                )}
              </>
            )}
          </span>
        ),
        width: '15%'
      },
      {
        Header: <FormattedMessage id="events.targeted_areas" defaultMessage="Targeted Areas" />,
        isSorted: true,
        accessor: 'targeted_areas',
        Cell: ({ row: { original } }) => (
          <span>
            {original?.display_targeted_areas?.map((val, idx) => {
              return (
                <span key={idx} className="border border-radius d-block p-1 mb-1">
                  <span className="d-block">{val.type}</span>
                  <span>
                    {val.provinces
                      ? val.provinces.map((province) => (
                          <Badge key={province.id} className="me-1" bg="primary">
                            {lang === 'ne' ? province.title : province.title_en}
                          </Badge>
                        ))
                      : null}
                  </span>
                  <span>
                    {val.districts
                      ? val.districts.map((dist) => (
                          <Badge key={dist.id} className="me-1" bg="primary">
                            {lang === 'ne' ? dist.title : dist.title_en}
                          </Badge>
                        ))
                      : null}
                  </span>
                  <span>
                    {val.local_level
                      ? val.local_level.map((lol) => (
                          <Badge key={lol.id} className="me-1" bg="primary">
                            {lang === 'ne' ? lol.title : lol.title_en}
                          </Badge>
                        ))
                      : null}
                  </span>
                </span>
              );
            })}
          </span>
        ),
        width: '12%'
      },
      {
        Header: <FormattedMessage id="location" />,
        isSorted: true,
        accessor: 'location',
        Cell: ({ row: { original } }) => (
          <span className="d-block">
            <span className="d-block">{original?.display_location?.at}</span>
            <span className="d-block">
              {original?.display_location?.virtual_link ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={original?.display_location?.virtual_link}
                  className="link-primary">
                  Virtual
                </a>
              ) : null}
            </span>
          </span>
        ),
        width: '11%'
      },
      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'actions',
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row: { original } }) => (
          <div className="d-flex">
            <div>
              <Link to={`${original.id}`}>
                <Button className="action-button" variant="outline-info">
                  <FaEye />
                </Button>
              </Link>
            </div>
            {activeTab !== 'public_events' && !original.completed && (
              <>
                {checkPermission(userActiveRole, 'edit event') && (
                  <div className="ms-2">
                    <Link to={`${original.id}/edit/0`}>
                      <Button className="action-button" variant="outline-success ">
                        <FaRegEdit />
                      </Button>
                    </Link>
                  </div>
                )}

                {checkPermission(userActiveRole, 'suspend event') && (
                  <div className="ms-2">
                    <Button
                      className="action-button"
                      variant="outline-danger "
                      onClick={() => onSuspendClick(original.id)}>
                      <IoArchiveOutline />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ),
        width: '10%'
      }
    ],
    [lang, userActiveRole, activeTab]
  );

  const data = useMemo(() => {
    if (!filteredEvents) return [];
    return filteredEvents.map((event) => {
      let thematicAreas = [];
      let value_targeted_local_gov = '';
      let value_targeted_for = [];
      if (lang === 'ne') {
        thematicAreas = event?.thematic_areas?.map((area) => area.title_nepali);
        event.targeted_local_gov.forEach((target) => {
          if (target.provinces) {
            target.provinces.forEach((province) => {
              value_targeted_local_gov += province.title + ',';
            });
          } else if (target.local_level) {
            target.local_level.forEach((local_level) => {
              value_targeted_local_gov += local_level.title + ',';
            });
          }
        });
      } else {
        thematicAreas = event?.thematic_areas?.map(
          (area) => area.title_english || area.title_nepali
        );
        event.targeted_local_gov.forEach((target) => {
          if (target.provinces) {
            target.provinces.forEach((province) => {
              value_targeted_local_gov += province.title_en + ',';
            });
          } else if (target.local_level) {
            target.local_level.forEach((local_level) => {
              value_targeted_local_gov += local_level.title_en + ',';
            });
          }
        });
      }
      value_targeted_for = event?.expected_participant_sector?.map((sector) => sector?.title);
      return {
        id: event.id,
        eventName: lang === 'ne' ? event.title_nepali : event.title_english,
        english_name: event.title_english,
        nepali_name: event.title_nepali,
        start_date: event.start_date,
        completed: event.completed,
        end_date: event.end_date,
        mtot: event.mtot,
        seasons: event.sessions,
        targeted_for: value_targeted_for,
        display_targeted_for: event.expected_participant_sector,
        organizer: `${event.created_by?.user}${event.created_by?.employee_type}`,
        organizer_employeeType: event.created_by?.employee_type,
        organizer_province: event.created_by?.province,
        organizer_user: event.created_by?.user,
        thematic_areas: thematicAreas,
        targeted_areas: value_targeted_local_gov,
        display_targeted_areas: event.targeted_local_gov,
        location: `${event?.physical_location_details?.tole || ''} ${
          event?.physical_location_details?.ward || ''
        } ${event?.physical_location_details?.municipality || ''} ${
          event?.physical_location_details?.district || ''
        } ${event?.physical_location_details?.province || ''}`,
        display_location: {
          virtual_link: event.virtual_link,
          at: `${event?.physical_location_details?.tole || ''} ${
            event?.physical_location_details?.ward || ''
          } ${event?.physical_location_details?.municipality || ''} ${
            event?.physical_location_details?.district || ''
          } ${event?.physical_location_details?.province || ''}`
        }
      };
    });
  }, [lang, filteredEvents]);

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    nextPage,
    previousPage,
    visibleColumns,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      pageCount: pageNum,
      defaultCanFilter: false,
      defaultColumn,
      disableSortBy: false
      // manualFilters: true
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );
  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount, pageSize]
  );

  const handleFilterClose = () => {
    setShowFilter(false);
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      let url = '';
      if (activeTab === 'my_events') {
        url = `/my-events/?is_suspended=false`;
      } else {
        url = `/event/?is_suspended=false`;
      }
      let params = paramsGenerator({
        pageSize: pageSize,
        searchEventParam: searchEventParam,
        pageNum: pageNum,
        eventState: eventStateType,
        eventType: eventFilters.EventType,
        isMtot: eventFilters.IsMtot,
        isPhysical: eventFilters.IsPhysical,
        isVirtual: eventFilters.IsVirtual,
        organizer: eventFilters.Organizer,
        thematicAreas: eventFilters.ThematicAreas,
        targetedProvinces: eventFilters.TargetedProvinces,
        sectors: eventFilters.Sectors
      });

      url = url + params;
      const response = await fetchWrapper.get(url);
      setFilteredEvents(response.results || response.data?.results || response.data?.data);
      setTotalCount(response?.data?.count || response.count);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };
  useEffect(() => {
    fetchEvents();
  }, [activeTab, searchEventParam, pageSize, pageNum, eventStateType, eventFilters]);

  const handleActiveTabChange = (tab) => {
    setActiveTab(tab);
  };
  const handleEventsFetch = ({
    IsMtot,
    IsPhysical,
    IsVirtual,
    EventType,
    Organizer,
    TargetedProvinces,
    ThematicAreas,
    Sectors
  }) => {
    setEventFilters({
      IsMtot: IsMtot,
      IsPhysical: IsPhysical,
      IsVirtual: IsVirtual,
      EventType: EventType,
      Organizer: Organizer,
      ThematicAreas: ThematicAreas,
      TargetedProvinces: TargetedProvinces,
      Sectors: Sectors
    });
  };

  const isEventsFiltered = useMemo(() => {
    if (
      !eventFilters.IsMtot &&
      !eventFilters.IsVirtual &&
      !eventFilters.IsPhysical &&
      !eventFilters.EventType &&
      !eventFilters.Organizer &&
      eventFilters.ThematicAreas.length === 0 &&
      eventFilters.TargetedProvinces.length === 0 &&
      eventFilters.Sectors.length === 0
    ) {
      return false;
    } else {
      return true;
    }
  }, [eventFilters]);

  useEffect(() => {
    const eventNavItemGenerator = () => {
      let navItems = [
        {
          title: 'my_events',
          link: 'my_events'
        }
      ];
      switch (user.active_role) {
        case 'superuser':
          navItems.push({
            title: 'all_events',
            link: 'all_events'
          });
          break;
        case 'cd_admin':
          navItems.push({
            title:
              lang === 'ne'
                ? `${user?.province?.title} कार्यक्रमहरु`
                : `${user?.province?.title_en} Events`,
            link: 'province_events',
            dontTranslate: true
          });
          break;

        case 'executive':
        case 'resource_person':
        case 'participant':
          navItems.push({
            title: `public_events`,
            link: 'public_events'
          });
          break;
        default:
          break;
      }

      setNavItems(navItems);
    };
    if (user) {
      eventNavItemGenerator();
    }
  }, [user, lang]);

  const onEventTypeChange = (eventType) => {
    setEventStateType(eventType);
  };

  const handleEventSearch = async (e) => {
    let typedTitle = e.target.value;
    if (typedTitle) {
      let eventSearchParam = `&${lang === 'ne' ? 'title_nepali' : 'title_english'}=${typedTitle}`;
      setSearchEventParam(eventSearchParam);
    } else {
      setSearchEventParam(false);
    }
  };

  const handleSuspend = async (eventId) => {
    try {
      await fetchWrapper.get(`/event/${eventId}/suspend/`);
      fetchEvents();
    } catch (err) {
      console.log(err);
    }
  };
  const onSuspendClick = (eventId) => {
    Swal.fire({
      title:
        lang === 'ne'
          ? 'के तपाइँ यो कार्यक्रम स्थगित गर्न निश्चित हुनुहुन्छ?'
          : 'Are you sure you want to Suspend this event?',
      text:
        lang === 'ne'
          ? 'स्थगित कार्यक्रम अन्य प्रयोगकर्ताहरूले देख्न सक्ने छैनन् ।'
          : "Suspeneded Events won't be visible to other users.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: lang === 'ne' ? 'स्थगित गर्नुहोस्!' : 'Yes, Suspend it!',
      cancelButtonText: lang === 'ne' ? 'रद्द गर्नुहोस्' : 'Cancel',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        handleSuspend(eventId);
        Swal.fire(
          lang === 'ne' ? 'स्थगित' : 'Suspended!',
          lang === 'ne' ? 'कार्यक्रम स्थगित गरिएको छ' : 'Event has been suspended.',
          'success'
        );
      }
    });
  };

  return (
    <div className="card">
      <div className="card-header stickyTop">
        <div className="row">
          <div className="col-4 d-flex">
            <EventsTableNav
              key={activeTab}
              activeTab={activeTab}
              navItems={navItems}
              handleActiveTabChange={handleActiveTabChange}
            />
          </div>
          <div className="col-4 d-flex align-items-center">
            <SearchBox
              className="large"
              placeholder={lang === 'ne' ? 'कार्यक्रम खोज्नुहोस' : 'Search Event'}
              onChange={debounce(handleEventSearch, 800)}
            />
            <Button
              variant="outline-info"
              id="filter-dropdown"
              className="mx-2"
              onClick={() => setShowFilter(true)}>
              <FaFilter />
            </Button>
          </div>
          <div className="col-4 d-flex justify-content-end align-items-center">
            {checkPermission(userActiveRole, 'view suspended events') && (
              <Link
                to={'suspended'}
                variant="outline-danger"
                className={`btn btn-danger text-light`}>
                <span className="d-flex align-items-center justify-content-center">
                  <IoArchiveOutline className="me-2" />
                  <FormattedMessage id="suspended_events" />
                </span>
              </Link>
            )}
            {permissions['Can add event'] && (
              <CreateEventLink to="new/0" className="btn btn-primary ms-2">
                <span className="d-flex align-items-center justify-content-center">
                  <FaPlus className="me-2" />
                  <FormattedMessage id="create_events" />
                </span>
              </CreateEventLink>
            )}
          </div>
        </div>
        {isEventsFiltered && (
          <div className="col-11 p-2 my-2 mx-3 d-flex align-items-center">
            <span className="text-dark font-weight-bold">Event filters:</span>
            <div className="d-flex flex-wrap">
              {eventFilters.IsMtot && (
                <FilterBadge
                  title={'mtot'}
                  clearFilter={() => {
                    setEventFilters({ ...eventFilters, IsMtot: '' });
                  }}
                />
              )}
              {eventFilters.IsPhysical && (
                <FilterBadge
                  title={'physical'}
                  clearFilter={() => {
                    setEventFilters({ ...eventFilters, IsPhysical: '' });
                  }}
                />
              )}
              {eventFilters.IsVirtual && (
                <FilterBadge
                  title={'virtual'}
                  clearFilter={() => {
                    setEventFilters({ ...eventFilters, IsVirtual: '' });
                  }}
                />
              )}
              {eventFilters.EventType && (
                <FilterBadge
                  title={eventFilters.EventType}
                  clearFilter={() => {
                    setEventFilters({ ...eventFilters, EventType: '' });
                  }}
                />
              )}
              {eventFilters.Organizer && (
                <FilterBadge
                  title={eventFilters.Organizer?.title}
                  clearFilter={() => {
                    setEventFilters({ ...eventFilters, Organizer: '' });
                  }}
                />
              )}
              {eventFilters.ThematicAreas &&
                eventFilters.ThematicAreas.map((area) => (
                  <FilterBadge
                    key={area.id}
                    title={area.title}
                    clearFilter={() => {
                      let tempThematicAreas = [...eventFilters.ThematicAreas];
                      tempThematicAreas = tempThematicAreas.filter(
                        (thematicArea) => thematicArea.id !== area.id
                      );
                      setEventFilters({ ...eventFilters, ThematicAreas: tempThematicAreas });
                    }}
                  />
                ))}
              {eventFilters.TargetedProvinces &&
                eventFilters.TargetedProvinces.map((province) => (
                  <FilterBadge
                    key={province.id}
                    title={province.title}
                    clearFilter={() => {
                      let tempProvince = [...eventFilters.TargetedProvinces];
                      tempProvince = tempProvince.filter((prov) => prov.id !== province.id);
                      setEventFilters({ ...eventFilters, TargetedProvinces: tempProvince });
                    }}
                  />
                ))}
              {eventFilters.Sectors &&
                eventFilters.Sectors.map((sector) => (
                  <FilterBadge
                    key={sector.id}
                    title={sector.title}
                    clearFilter={() => {
                      let tempSector = [...eventFilters.Sectors];
                      tempSector = tempSector.filter((sec) => sec.id !== sector.id);
                      setEventFilters({ ...eventFilters, Sectors: tempSector });
                    }}
                  />
                ))}
            </div>
            <span
              className="text-small cursor-pointer text-primary ms-2"
              onClick={() => {
                setEventFilters(INITIALSTATE);
              }}>
              Clear All
            </span>
          </div>
        )}
      </div>
      <div className="card-body pt-0">
        <ReactTableRender
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          getTableBodyProps={getTableBodyProps}
          page={page}
          prepareRow={prepareRow}
          totalCount={totalCount}
          gotoPage={gotoPage}
          previousPage={previousPage}
          pageCount={pageCount}
          pageCountArray={pageCountArray}
          pageNum={pageNum}
          showGoto={true}
          nextPage={nextPage}
          pageIndex={pageIndex}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          loading={loading}
          visibleColumns={visibleColumns}
          pagination={true}
          pageSize={pageSize}
          setPageSize={setPageSize}
          leftChildren={
            <div className="w-25 pe-5 ps-1">
              <Select
                value={eventStateType}
                options={options}
                onChange={(e) => onEventTypeChange(e)}
              />
            </div>
          }
        />
        {showFilter && (
          <FilterOffCanvas
            show={showFilter}
            handleClose={handleFilterClose}
            handleEventsFetch={handleEventsFetch}
            eventFilters={eventFilters}
          />
        )}
      </div>
    </div>
  );
}

export default EventsTable;
const options = [
  {
    value: '',
    label: <FormattedMessage id="eventtab.allEvents" />
  },
  {
    value: 'ongoing',
    label: <FormattedMessage id="ongoingEvents" />
  },
  {
    value: 'upcoming',
    label: <FormattedMessage id="upcomingEvents" />
  },
  {
    value: 'completed',
    label: <FormattedMessage id="completedEvents" />
  }
];

const TargetedForSelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id }
}) => {
  const options = useMemo(() => {
    const options = new Set();
    if (preFilteredRows) {
      preFilteredRows.forEach((row) => {
        row.values[id]?.forEach((value) => options.add(value));
      });
    }
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <TableSelect
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}>
      <FormattedMessage id="tableSelect.all">
        {(option) => <option value="">{option[0]}</option>}
      </FormattedMessage>

      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </TableSelect>
  );
};

const ThemanticAreaSelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id }
}) => {
  const options = useMemo(() => {
    const options = new Set();
    if (preFilteredRows) {
      preFilteredRows.forEach((row) => {
        row.values[id]?.forEach((value) => {
          options.add(value);
        });
      });
    }
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <TableSelect
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}>
      <FormattedMessage id="tableSelect.all">
        {(option) => <option value="">{option[0]}</option>}
      </FormattedMessage>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </TableSelect>
  );
};
